/**
 * src/pages/[section]/index.tsx
 *
 * Category home / index — aggregates everything in a section for the whole
 * site: internal pages, external source links (from each page's media), the
 * image + PDF galleries, and cross-site related content (TF-IDF).
 */
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import fs from 'fs';
import path from 'path';
import {
  generateFullPageMetadata,
  PageHeader,
  SocialShareButtons,
  Related,
  Library,
  Body,
} from './../../components';
import { build_content } from './../../utils/content_parser';
import type { RelatedItem } from './../../components';
import { getPagePaths, resolveThumbnailInfoJson } from './../../functions';
import { getAllPageDefinitions } from './../../utils/contentWalker';
import { derivePageDates } from './../../utils/page_dates';
import type { ImageData, PageData } from '@interfaces';
import styles from './../../styles/CategoryIndex.module.css';

interface SourceLink {
  title: string;
  url: string;
  siteName?: string;
  image?: string;
}

interface ResourceLink {
  label: string;
  href: string;
}

interface CategoryProps {
  pageData: PageData;
  library?: any;
  imageData: ImageData;
  internal: RelatedItem[];
  sources: SourceLink[];
  resources: ResourceLink[];
  relatedAcross: RelatedItem[];
}

const CategoryIndex: NextPage<CategoryProps> = ({
  pageData,
  imageData,
  library,
  internal,
  sources,
  resources,
  relatedAcross,
}) => {
  const metadata = generateFullPageMetadata(pageData, imageData);

  return (
    <>
      <Head>
        <title>{metadata.pageTitle}</title>
        <link rel="canonical" href={metadata.canonicalUrl} />
        {Object.entries(metadata.meta).map(([name, content]) => (
          <meta key={name} name={name} content={content} />
        ))}
        {Object.entries(metadata.openGraph).map(([property, value]) => {
          if (Array.isArray(value)) {
            return value.map((v, i) => (
              <meta key={`${property}-${i}`} property={property} content={v} />
            ));
          }
          return <meta key={property} property={property} content={value as string} />;
        })}
        {Object.entries(metadata.twitter).map(([name, content]) => (
          <meta key={name} name={name} content={content} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(metadata.schemaGraph) }}
        />
      </Head>

      <SocialShareButtons
        text={pageData.description}
        url={pageData.share_url}
        via="thedailydialectics"
        hashtags={pageData.keywords_str}
        title={pageData.title}
        description={pageData.description}
        summary={pageData.summary}
        image={metadata.primaryImage}
      />

      <PageHeader meta={pageData} baseUrl={pageData.BASE_URL} />

      {library ? <Library data={library} /> : null}
      {pageData.content ? <Body meta={pageData} /> : null}

      <Related items={internal} heading={library ? 'More in this section' : 'Pages in this category'} />

      {resources.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.heading}>Galleries &amp; documents</h2>
          <div className={styles.resourceRow}>
            {resources.map((r) => (
              <a key={r.href} className={styles.resource} href={r.href}>
                {r.label}
              </a>
            ))}
          </div>
        </section>
      )}

      {sources.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.heading}>External sources</h2>
          <ul className={styles.sourceList}>
            {sources.map((s) => (
              <li key={s.url}>
                <a href={s.url} target="_blank" rel="noopener noreferrer nofollow">
                  <span className={styles.sourceTitle}>{s.title}</span>
                  {s.siteName ? <span className={styles.sourceSite}>{s.siteName}</span> : null}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Related items={relatedAcross} heading="Related across the site" />
    </>
  );
};

// ── helpers ────────────────────────────────────────────────────────────────

const titleCase = (s: string) =>
  s.split(/[-_]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

function readPage(section: string, slug: string) {
  try {
    const { jsonPath } = getPagePaths(section, slug);
    const vars: PageData = JSON.parse(fs.readFileSync(jsonPath, 'utf8'));
    const pageDir = path.dirname(jsonPath);
    let image = '';
    let imageData: any = null;
    try {
      const infoPath = resolveThumbnailInfoJson(vars.thumbnail, pageDir);
      imageData = JSON.parse(fs.readFileSync(infoPath, 'utf8'));
      image = imageData?.schema?.url || imageData?.social_meta?.['og:image'] || '';
    } catch {
      /* thumbnail optional */
    }
    return { vars, image, imageData };
  } catch {
    return { vars: null as any, image: '', imageData: null };
  }
}

// ── data ──────────────────────────────────────────────────────────────────

export const getStaticPaths: GetStaticPaths = async () => {
  const sections = Array.from(
    new Set(getAllPageDefinitions().map((p) => p.section)),
  ).filter((s) => s !== 'home');
  return {
    paths: sections.map((section) => ({ params: { section } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    const section = params?.section as string;
    const BASE = process.env.NEXT_PUBLIC_API_BASE?.replace(/\/+$/, '') || 'https://thedailydialectics.com';

    const pages = getAllPageDefinitions()
      .filter((p) => p.section === section)
      .sort((a, b) => a.slug.localeCompare(b.slug));
    if (pages.length === 0) return { notFound: true };

    // Content layout: {media}/pages, {media}/imgs, {media}/pdfs are siblings.
    // Derive them from a page path so the gallery links work without relying on
    // build-time env, but allow PUBLIC_ROOT / PDFS_ROOT to override.
    const { jsonPath: anyJsonPath } = getPagePaths(section, pages[0].slug);
    const pagesRoot = path.dirname(path.dirname(path.dirname(anyJsonPath)));
    // Frozen media (imgs/pdfs) lives under MEDIA_ROOT, separate from the
    // content tree. Falls back to the pages-root sibling for the old layout.
    const mediaRoot = process.env.MEDIA_ROOT || path.dirname(pagesRoot);
    const imgsRoot = path.join(mediaRoot, 'imgs');
    const pdfsRoot = process.env.PDFS_ROOT || path.join(mediaRoot, 'pdfs');

    // Internal page cards + external sources from each page's media.
    const internal: RelatedItem[] = [];
    const sourceMap = new Map<string, SourceLink>();
    let hero: { vars: PageData; image: string; imageData: any; slug: string } | null = null;

    for (const { slug } of pages) {
      const { vars, image, imageData } = readPage(section, slug);
      if (!vars) continue;
      const href = vars.share_url || `${BASE}/${section}/${slug}`;
      internal.push({ section, slug, title: vars.title || titleCase(slug), href, image });
      // Hero priority: slug==section, then an explicit section_landing flag in
      // variables.json, then the first page alphabetically.
      const heroRank = (v: PageData | null, s: string) =>
        s === section ? 2 : v?.section_landing ? 1 : 0;
      if (!hero || heroRank(vars, slug) > heroRank(hero.vars, hero.slug)) {
        hero = { vars, image, imageData, slug };
      }

      for (const m of (vars.media as any[]) || []) {
        const url = m?.url;
        if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) continue;
        const type = m?.type || '';
        if (type && !['article', 'website', 'document'].includes(type)) continue;
        if (url.includes('thedailydialectics.com')) continue;
        if (!sourceMap.has(url)) {
          sourceMap.set(url, {
            title: m.title || m.siteName || url,
            url,
            siteName: m.siteName || m.source_preview?.site_name || '',
            image: m.thumbnail || m.source_preview?.image || '',
          });
        }
      }
    }

    // If any page in this section carries a library.json (the "Reading Room"
    // archive, e.g. education/educational), promote it to BE the section page:
    // render its catalog + prose here and drop its card from the list, so
    // /education shows the archive and /education/educational just redirects in.
    let sectionLibrary: any = null;
    let sectionContentHtml = '';
    let primarySlug = '';
    try {
      for (const { slug } of pages) {
        const { jsonPath, contentsPath } = getPagePaths(section, slug);
        const dir = path.dirname(jsonPath);
        const libPath = path.join(dir, 'library.json');
        if (fs.existsSync(libPath)) {
          sectionLibrary = JSON.parse(fs.readFileSync(libPath, 'utf8'));
          primarySlug = slug;
          const primaryVars: PageData = JSON.parse(fs.readFileSync(jsonPath, 'utf8'));
          if (fs.existsSync(contentsPath)) {
            const raw = fs.readFileSync(contentsPath, 'utf8');
            sectionContentHtml = await build_content(raw, primaryVars, slug);
          }
          break;
        }
      }
    } catch (e) {
      console.error('section library adoption error:', e);
    }

    const internalCards = primarySlug ? internal.filter((c) => c.slug !== primarySlug) : internal;
    const sources = Array.from(sourceMap.values()).slice(0, 80);

    // Image / PDF galleries — only link to ones we can confirm exist.
    const resources: ResourceLink[] = [];
    try {
      if (fs.existsSync(path.join(imgsRoot, section))) {
        resources.push({ label: 'Image gallery', href: `${BASE}/imgs/${section}/` });
      }
      if (fs.existsSync(path.join(pdfsRoot, section))) {
        resources.push({ label: 'PDF documents', href: `${BASE}/pdfs/${section}/` });
      }
    } catch {
      /* best effort */
    }

    // Cross-site related: union of every section page's related, minus pages
    // already listed in this category.
    const relatedAcross: RelatedItem[] = [];
    try {
      const idxPath = path.join(pagesRoot, 'related_index.json');
      if (fs.existsSync(idxPath)) {
        const idx = JSON.parse(fs.readFileSync(idxPath, 'utf8')) as Record<string, any>;
        const own = new Set(pages.map((p) => `${section}/${p.slug}`));
        const seen = new Set<string>();
        const acc: Array<RelatedItem & { score: number }> = [];
        for (const { slug } of pages) {
          const entry = idx[`${section}/${slug}`];
          for (const r of entry?.related || []) {
            const key = `${r.section}/${r.slug}`;
            if (own.has(key) || seen.has(key)) continue;
            seen.add(key);
            acc.push({ ...r, score: r.score || 0 });
          }
        }
        acc.sort((a, b) => b.score - a.score);
        relatedAcross.push(
          ...acc.slice(0, 12).map((r) => ({
            section: r.section,
            slug: r.slug,
            title: r.title,
            href: r.href,
            image: r.image || '',
            score: r.score || 0,
          })),
        );
      }
    } catch (e) {
      console.error('category related_index error:', e);
    }

    // Real dates for the section index: earliest publish / latest modification
    // across the section's pages (same utils/page_dates derivation the slug
    // pages use — explicit variables.json field, else content-file mtimes).
    let sectionPublished: string | null = null;
    let sectionModified: string | null = null;
    for (const { slug } of pages) {
      try {
        const { jsonPath, contentsPath } = getPagePaths(section, slug);
        const d = derivePageDates(jsonPath, contentsPath);
        if (!d) continue;
        if (!sectionPublished || d.datePublished < sectionPublished) sectionPublished = d.datePublished;
        if (!sectionModified || d.dateModified > sectionModified) sectionModified = d.dateModified;
      } catch {
        /* per-page dates are best effort */
      }
    }

    // Synthesize the section's pageData for the hero + metadata.
    const title = titleCase(section);
    const description =
      hero?.vars?.description ||
      `Everything on ${title} at The Daily Dialectics — pages, documents, images and sources.`;
    const pageData: PageData = {
      BASE_URL: hero?.vars?.BASE_URL || BASE,
      href: `/${section}`,
      title,
      share_url: `${BASE}/${section}`,
      description,
      keywords_str: hero?.vars?.keywords_str || '',
      thumbnail: hero?.vars?.thumbnail || '',
      ...(sectionPublished ? { datePublished: sectionPublished } : {}),
      ...(sectionModified ? { dateModified: sectionModified } : {}),
      // Breadcrumb trail (Home → Section) for BreadcrumbList JSON-LD.
      breadcrumbs: [
        { name: 'Home', item: `${BASE}/` },
        { name: title, item: `${BASE}/${section}` },
      ],
      // Only include imageData when present — Next can't serialize `undefined`.
      ...(hero?.imageData ? { imageData: hero.imageData } : {}),
    };
    const imageData: ImageData = (hero?.imageData || {}) as ImageData;

    if (sectionContentHtml) (pageData as any).content = sectionContentHtml;
    return { props: { pageData, imageData, library: sectionLibrary, internal: internalCards, sources, resources, relatedAcross } };
  } catch (error) {
    console.error('Category index build error:', error);
    return { notFound: true };
  }
};

export default CategoryIndex;
